import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [honeypot, setHoneypot] = useState(''); // 🍯 Honeypot voor bot detectie
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Honeypot check: als dit veld is ingevuld, is het een bot
    if (honeypot) {
      console.warn('🍯 Bot detected via honeypot field');
      toast.error('Registratie mislukt');
      return;
    }

    // CAPTCHA verificatie
    if (!turnstileToken) {
      toast.error('Verifieer dat je geen robot bent');
      return;
    }
    
    if (!email || !password || !confirmPassword) {
      toast.error('Vul alle velden in');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }

    if (password.length < 6) {
      toast.error('Wachtwoord moet minimaal 6 karakters zijn');
      return;
    }

    setLoading(true);
    
    try {
      // Roep de backend verificatie functie aan met CAPTCHA token
      const response = await fetch('/.netlify/functions/verify-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          turnstileToken,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        toast.error('Registreren mislukt: ' + (data.error || 'Onbekende fout'));
      } else {
        toast.success('Account aangemaakt! Je kunt nu inloggen met je zojuist aangemaakte account.');
        navigate('/login');
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('Registreren mislukt. Probeer het later opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-subtle px-4 py-8 md:py-12">
      <div className="max-w-md w-full space-y-6 md:space-y-8 bg-white p-6 md:p-8 rounded-xl shadow-lg">
        <div className="text-center">
          <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: '#6b4912' }}>Registreren</h1>
          <p className="text-sm md:text-base" style={{ color: '#6b4912' }}>Maak een nieuw account aan</p>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 md:mt-8 space-y-5 md:space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm md:text-base">E-mailadres</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jouw@email.nl"
                required
                className="mt-1 text-base"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm md:text-base">Wachtwoord</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1 text-base"
              />
              <p className="text-xs mt-1" style={{ color: '#6b4912' }}>Minimaal 6 karakters</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-sm md:text-base">Bevestig wachtwoord</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1 text-base"
              />
            </div>

            {/* 🍯 Honeypot veld: onzichtbaar voor mensen, zichtbaar voor bots */}
            <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
              <Input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                aria-label="Laat dit veld leeg"
              />
            </div>
          </div>

          {/* 🔒 Cloudflare Turnstile CAPTCHA */}
          <div className="flex justify-center">
            <Turnstile
              siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
              onSuccess={(token) => setTurnstileToken(token)}
              onError={() => {
                toast.error('CAPTCHA verificatie mislukt');
                setTurnstileToken(null);
              }}
              onExpire={() => setTurnstileToken(null)}
            />
          </div>

          <Button
            type="submit"
            disabled={loading || !turnstileToken}
            className="w-full text-base"
          >
            {loading ? 'Bezig met registreren...' : 'Registreren'}
          </Button>
        </form>

        <div className="text-center text-sm">
          <span style={{ color: '#6b4912' }}>Al een account? </span>
          <Link to="/login" className="hover:underline font-medium" style={{ color: '#854c73' }}>
            Log hier in
          </Link>
        </div>

        <div className="text-center">
          <Link to="/" className="text-sm hover:text-primary" style={{ color: '#6b4912' }}>
            ← Terug naar home
          </Link>
        </div>
      </div>
    </div>
  );
}

